import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, Upload, Trash2, Plus, Loader2, FileText, User, AlertTriangle,
  CheckCircle2, BellRing, BellOff, Bell, Clock, X, ExternalLink, Calendar, Smartphone,
} from "lucide-react";
import { api, errMsg } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { usePushNotifications } from "@/lib/pushNotifications";

const STATUS_STYLE = {
  ok:       { label: "Regolare",      color: "text-[#22c55e]", bg: "bg-[#22c55e]/10", border: "border-[#22c55e]/25" },
  notice:   { label: "≤ 90 giorni",   color: "text-[#eab308]", bg: "bg-[#eab308]/10", border: "border-[#eab308]/25" },
  warning:  { label: "≤ 60 giorni",   color: "text-[#f97316]", bg: "bg-[#f97316]/10", border: "border-[#f97316]/25" },
  critical: { label: "≤ 30 giorni",   color: "text-[#ef4444]", bg: "bg-[#ef4444]/10", border: "border-[#ef4444]/25" },
  expired:  { label: "SCADUTO",       color: "text-[#ef4444]", bg: "bg-[#ef4444]/15", border: "border-[#ef4444]/40" },
  unknown:  { label: "—",             color: "text-white", bg: "bg-white/5", border: "border-white/10" },
};

export default function AdminSafety() {
  const { user } = useAuth();
  const isTechnician = user?.role === "technician";
  const [certs, setCerts] = useState([]);
  const [team, setTeam] = useState([]);
  const [certTypes, setCertTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTech, setSelectedTech] = useState("");
  const [modal, setModal] = useState(null); // {mode:'new'|'edit', cert?}
  const [notifying, setNotifying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, tp] = await Promise.all([
        api.get(`/admin/safety/certificates${selectedTech ? `?technician_id=${selectedTech}` : ""}`),
        api.get("/admin/safety/cert-types"),
      ]);
      setCerts(c.data || []);
      setCertTypes(tp.data || []);
      if (!isTechnician) {
        const t = await api.get("/admin/team");
        setTeam(t.data || []);
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore caricamento attestati"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [selectedTech]);

  const runReminders = async () => {
    if (!window.confirm("Invia i promemoria di scadenza (30/60/90 giorni e scaduti) ai tecnici via email?")) return;
    setNotifying(true);
    try {
      const r = await api.post("/admin/safety/notify");
      if (r.data.count === 0) toast.info("Nessun promemoria da inviare — tutti gli attestati sono in regola.");
      else toast.success(`${r.data.count} promemoria inviati / accodati`);
    } catch (e) {
      toast.error(errMsg(e, "Errore invio promemoria"));
    } finally {
      setNotifying(false);
    }
  };

  const remove = async (cert) => {
    if (!window.confirm(`Eliminare l'attestato "${cert.title}"?`)) return;
    try {
      await api.delete(`/admin/safety/certificates/${cert.id}`);
      toast.success("Attestato eliminato");
      load();
    } catch (e) { toast.error(errMsg(e, "Errore eliminazione")); }
  };

  const totalExpiring = certs.filter(c => ["notice", "warning", "critical", "expired"].includes(c.status)).length;
  const totalExpired = certs.filter(c => c.status === "expired").length;

  return (
    <div data-testid="safety-panel">
      <div className="flex items-start justify-between mb-8 gap-6 flex-wrap">
        <div>
          <div className="num-chip mb-3"><span className="dot" /> Compliance & Sicurezza</div>
          <div className="font-display text-3xl md:text-4xl">Attestati sicurezza</div>
          <div className="text-white text-sm mt-2 max-w-xl">
            {isTechnician
              ? "I tuoi attestati di sicurezza e le relative scadenze. Riceverai promemoria via email quando una scadenza si avvicina."
              : "Carica gli attestati RSPP, HACCP, Antincendio, ecc. dei tecnici con le date di scadenza. Il sistema invia automaticamente promemoria via email a 90/60/30 giorni."}
          </div>
        </div>
        {!isTechnician && (
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={runReminders}
              disabled={notifying}
              data-testid="safety-run-reminders"
              className="btn-ghost text-[11px]"
              title="Invia i promemoria di scadenza in coda"
            >
              {notifying ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />}
              Invia promemoria
            </button>
            <button
              onClick={() => setModal({ mode: "new" })}
              data-testid="safety-new-cert"
              className="btn-primary"
            >
              <Plus size={14} /> Nuovo attestato
            </button>
          </div>
        )}
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Totale" value={certs.length} tone="neutral" />
        <StatCard label="In regola" value={certs.filter(c => c.status === "ok").length} tone="ok" />
        <StatCard label="In scadenza" value={totalExpiring - totalExpired} tone="warning" />
        <StatCard label="Scaduti" value={totalExpired} tone="danger" />
      </div>

      {/* Push notifications toggle (visible to everyone) */}
      <PushToggleCard />

      {/* Filter by technician (admin only) */}
      {!isTechnician && team.length > 0 && (
        <div className="mb-5 flex items-center gap-3 flex-wrap">
          <span className="font-mono-tech text-[10px] uppercase text-white">Filtra per tecnico</span>
          <select
            value={selectedTech}
            onChange={(e) => setSelectedTech(e.target.value)}
            className="field"
            data-testid="safety-tech-filter"
          >
            <option value="">Tutti i tecnici</option>
            {team.map((t) => (
              <option key={t.id} value={t.id}>{t.name || t.email}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-white"><Loader2 size={20} className="animate-spin inline-block" /></div>
      ) : certs.length === 0 ? (
        <div className="border border-dashed border-[#1a1a1a] p-10 text-center">
          <ShieldCheck size={32} className="text-white mx-auto mb-3" />
          <div className="text-white text-sm">Nessun attestato registrato.</div>
          {!isTechnician && (
            <button onClick={() => setModal({ mode: "new" })} className="btn-primary mt-4 mx-auto">
              <Plus size={14} /> Aggiungi il primo attestato
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {certs.map((c) => (
            <CertCard key={c.id} cert={c} onEdit={!isTechnician ? () => setModal({ mode: "edit", cert: c }) : null} onDelete={!isTechnician ? () => remove(c) : null} />
          ))}
        </div>
      )}

      {modal && !isTechnician && (
        <CertModal
          mode={modal.mode}
          cert={modal.cert}
          team={team}
          certTypes={certTypes}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function PushToggleCard() {
  const push = usePushNotifications();
  const [testing, setTesting] = useState(false);

  if (!push.supported) {
    return (
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4 mb-6" data-testid="push-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border border-white/10 flex items-center justify-center text-white">
            <BellOff size={16} />
          </div>
          <div>
            <div className="font-mono-tech text-[10px] uppercase text-white">Notifiche Push</div>
            <div className="text-sm text-white">Il tuo browser non supporta le notifiche push.</div>
            <div className="text-[11px] text-white mt-1">Su iPhone: apri il sito da Safari → Condividi → Aggiungi a Home, poi apri l&apos;app dalla home.</div>
          </div>
        </div>
      </div>
    );
  }

  const doEnable = async () => {
    try {
      await push.enable();
      toast.success("Notifiche push attivate su questo dispositivo");
    } catch (e) {
      toast.error(e.message || "Impossibile attivare le notifiche");
    }
  };

  const doDisable = async () => {
    if (!window.confirm("Disattivare le notifiche push su questo dispositivo?")) return;
    try {
      await push.disable();
      toast.success("Notifiche push disattivate");
    } catch (e) {
      toast.error(errMsg(e, "Errore disattivazione"));
    }
  };

  const doTest = async () => {
    setTesting(true);
    try {
      const r = await push.sendTest();
      if (r.delivered > 0) toast.success(`Notifica di prova inviata (${r.delivered} dispositivi)`);
      else toast.info("Notifica accodata — se non arriva, verifica che le notifiche siano abilitate a livello di sistema operativo");
    } catch (e) {
      toast.error(errMsg(e, "Errore invio test"));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="border border-[#1a1a1a] bg-gradient-to-br from-[#0a0a0a] to-[#0a0a0a]/50 p-4 mb-6" data-testid="push-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 border flex items-center justify-center ${push.subscribed ? "border-[#22c55e]/30 bg-[#22c55e]/10 text-[#22c55e]" : "border-[#ff4b00]/30 bg-[#ff4b00]/5 text-[#ff4b00]"}`}>
            {push.subscribed ? <BellRing size={16} /> : <Bell size={16} />}
          </div>
          <div className="min-w-0">
            <div className="font-mono-tech text-[10px] uppercase text-white flex items-center gap-1.5">
              <Smartphone size={11} /> Notifiche push su questo dispositivo
            </div>
            <div className="text-white font-display text-base mt-0.5">
              {push.subscribed ? "Attive" : push.permission === "denied" ? "Bloccate dal browser" : "Non attive"}
            </div>
            <div className="text-[11px] text-white mt-1 max-w-md">
              {push.subscribed
                ? "Riceverai una notifica sul telefono quando un attestato entra nei 30 giorni o scade."
                : push.permission === "denied"
                  ? "Hai bloccato le notifiche. Sblocca dalle impostazioni del browser (icona lucchetto in alto)."
                  : "Ricevi un avviso sul telefono per non perdere le scadenze degli attestati."}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap shrink-0">
          {push.subscribed ? (
            <>
              <button
                onClick={doTest}
                disabled={testing}
                data-testid="push-test-btn"
                className="btn-ghost text-[11px]"
              >
                {testing ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />}
                Invia test
              </button>
              <button
                onClick={doDisable}
                disabled={push.loading}
                data-testid="push-disable-btn"
                className="btn-ghost text-[11px]"
              >
                {push.loading ? <Loader2 size={12} className="animate-spin" /> : <BellOff size={12} />}
                Disattiva
              </button>
            </>
          ) : (
            <button
              onClick={doEnable}
              disabled={push.loading || push.permission === "denied"}
              data-testid="push-enable-btn"
              className="btn-primary"
            >
              {push.loading ? <Loader2 size={14} className="animate-spin" /> : <BellRing size={14} />}
              Attiva notifiche
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneMap = {
    neutral: "border-[#1a1a1a] bg-[#0a0a0a] text-white",
    ok:      "border-[#22c55e]/25 bg-[#22c55e]/[0.05] text-[#22c55e]",
    warning: "border-[#eab308]/25 bg-[#eab308]/[0.05] text-[#eab308]",
    danger:  "border-[#ef4444]/25 bg-[#ef4444]/[0.05] text-[#ef4444]",
  };
  return (
    <div className={`border p-4 ${toneMap[tone]}`}>
      <div className="font-mono-tech text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-3xl font-display mt-1">{value}</div>
    </div>
  );
}

function CertCard({ cert, onEdit, onDelete }) {
  const st = STATUS_STYLE[cert.status] || STATUS_STYLE.unknown;
  return (
    <div className={`border ${st.border} ${st.bg} p-4`} data-testid={`safety-cert-${cert.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono-tech text-[10px] uppercase text-white flex items-center gap-1.5 mb-1">
            <User size={11} /> {cert.technician_name}
          </div>
          <div className="font-display text-lg text-white truncate">{cert.title}</div>
          <div className="font-mono-tech text-[10px] uppercase text-white mt-0.5">{cert.cert_type}{cert.issuer ? ` · ${cert.issuer}` : ""}</div>
        </div>
        <div className={`font-mono-tech text-[10px] uppercase tracking-wider ${st.color} border ${st.border} px-2 py-1 whitespace-nowrap`}>
          {st.label}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <div>
          <div className="font-mono-tech text-[9px] uppercase text-white">Rilascio</div>
          <div className="text-white">{cert.issue_date}</div>
        </div>
        <div>
          <div className="font-mono-tech text-[9px] uppercase text-white">Scadenza</div>
          <div className={`${cert.status === "expired" || cert.status === "critical" ? "text-[#ef4444] font-bold" : "text-white"}`}>
            {cert.expiry_date} {cert.days_left !== null && (
              <span className="font-mono-tech text-[10px] text-white">
                ({cert.days_left < 0 ? `${-cert.days_left}g fa` : `+${cert.days_left}g`})
              </span>
            )}
          </div>
        </div>
      </div>
      {cert.notes && (
        <div className="mt-2 text-xs text-white line-clamp-2">{cert.notes}</div>
      )}
      <div className="mt-3 flex items-center gap-2 flex-wrap pt-3 border-t border-white/5">
        {cert.file_url && (
          <a
            href={cert.file_url}
            target="_blank" rel="noopener noreferrer"
            data-testid={`safety-cert-open-${cert.id}`}
            className="btn-ghost text-[10px] py-1.5"
          >
            <FileText size={11} /> Apri PDF <ExternalLink size={10} />
          </a>
        )}
        {onEdit && (
          <button onClick={onEdit} data-testid={`safety-cert-edit-${cert.id}`} className="btn-ghost text-[10px] py-1.5">
            Modifica
          </button>
        )}
        {onDelete && (
          <button onClick={onDelete} data-testid={`safety-cert-delete-${cert.id}`} className="btn-ghost text-[10px] py-1.5 hover:!text-[#ef4444]">
            <Trash2 size={11} /> Elimina
          </button>
        )}
      </div>
    </div>
  );
}

function CertModal({ mode, cert, team, certTypes, onClose, onSaved }) {
  const [f, setF] = useState({
    technician_id: cert?.technician_id || "",
    cert_type: cert?.cert_type || certTypes[0] || "",
    title: cert?.title || "",
    issuer: cert?.issuer || "",
    issue_date: cert?.issue_date || new Date().toISOString().slice(0, 10),
    expiry_date: cert?.expiry_date || new Date(Date.now() + 365 * 86400_000).toISOString().slice(0, 10),
    notes: cert?.notes || "",
  });
  const [file, setFile] = useState(null);
  const [existingUrl, setExistingUrl] = useState(cert?.file_url || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.technician_id) { toast.error("Seleziona un tecnico"); return; }
    if (!f.cert_type) { toast.error("Seleziona il tipo di attestato"); return; }
    if (!f.issue_date || !f.expiry_date) { toast.error("Date obbligatorie"); return; }
    if (new Date(f.expiry_date) < new Date(f.issue_date)) { toast.error("La scadenza deve essere dopo il rilascio"); return; }

    setSaving(true);
    try {
      let fileUrl = existingUrl;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/admin/safety/certificates/upload-file", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        fileUrl = up.data.url;
      }
      const payload = {
        ...f,
        title: f.title.trim() || f.cert_type,
        issuer: f.issuer.trim() || null,
        notes: f.notes.trim() || null,
      };
      if (mode === "new") {
        await api.post(`/admin/safety/certificates${fileUrl ? `?file_url=${encodeURIComponent(fileUrl)}` : ""}`, payload);
        toast.success("Attestato registrato");
      } else {
        // PATCH excludes technician_id.
        const { technician_id: _tid, ...upd } = payload;
        await api.patch(`/admin/safety/certificates/${cert.id}${fileUrl && fileUrl !== cert.file_url ? `?file_url=${encodeURIComponent(fileUrl)}` : ""}`, upd);
        toast.success("Attestato aggiornato");
      }
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-[#0a0a0a] border border-[#1a1a1a] w-full max-w-2xl my-8" data-testid="safety-cert-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#1a1a1a]">
          <div>
            <div className="font-mono-tech text-[10px] uppercase text-white">◐ {mode === "new" ? "Nuovo attestato" : "Modifica attestato"}</div>
            <div className="font-display text-xl mt-1">Attestato di sicurezza</div>
          </div>
          <button type="button" onClick={onClose} className="text-white hover:text-white" data-testid="safety-cert-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="field-label">Tecnico *</label>
            <select
              value={f.technician_id}
              onChange={(e) => setF({ ...f, technician_id: e.target.value })}
              disabled={mode === "edit"}
              className="field mt-2 w-full"
              data-testid="safety-tech-select"
            >
              <option value="">Seleziona…</option>
              {team.map((t) => (
                <option key={t.id} value={t.id}>{t.name || t.email}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="field-label">Tipo di attestato *</label>
              <select
                value={f.cert_type}
                onChange={(e) => setF({ ...f, cert_type: e.target.value })}
                className="field mt-2 w-full"
                data-testid="safety-type-select"
              >
                {certTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Titolo</label>
              <input
                value={f.title}
                onChange={(e) => setF({ ...f, title: e.target.value })}
                placeholder={f.cert_type ? `Es. ${f.cert_type} - Modulo A` : "Titolo attestato"}
                className="field mt-2 w-full"
                data-testid="safety-title"
              />
            </div>
          </div>

          <div>
            <label className="field-label">Ente rilasciante</label>
            <input
              value={f.issuer}
              onChange={(e) => setF({ ...f, issuer: e.target.value })}
              placeholder="Es. Confartigianato Pordenone"
              className="field mt-2 w-full"
              data-testid="safety-issuer"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="field-label flex items-center gap-1.5"><Calendar size={11} /> Data rilascio *</label>
              <input
                type="date"
                value={f.issue_date}
                onChange={(e) => setF({ ...f, issue_date: e.target.value })}
                className="field mt-2 w-full"
                data-testid="safety-issue-date"
              />
            </div>
            <div>
              <label className="field-label flex items-center gap-1.5"><Clock size={11} /> Data scadenza *</label>
              <input
                type="date"
                value={f.expiry_date}
                onChange={(e) => setF({ ...f, expiry_date: e.target.value })}
                className="field mt-2 w-full"
                data-testid="safety-expiry-date"
              />
            </div>
          </div>

          <div>
            <label className="field-label">Note</label>
            <textarea
              value={f.notes}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
              rows={2}
              placeholder="Es. Codice corso, docente, ore formative…"
              className="field mt-2 w-full"
              data-testid="safety-notes"
            />
          </div>

          <div>
            <label className="field-label flex items-center gap-1.5"><Upload size={11} /> File attestato (PDF o immagine, max 15MB)</label>
            <input
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="field mt-2 w-full"
              data-testid="safety-file"
            />
            {existingUrl && !file && (
              <div className="mt-2 text-xs text-white flex items-center gap-2">
                <FileText size={12} className="text-[#ff4b00]" />
                <a href={existingUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">File corrente caricato</a>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 p-5 border-t border-[#1a1a1a]">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="safety-cert-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {mode === "new" ? "Registra attestato" : "Salva modifiche"}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
        </div>
      </form>
    </div>
  );
}
